/**
 * Batch-source panel for TransactionDetailModal.
 *
 * Shows the originating weekly batch, the account canonical name, and
 * (when present) the raw upline row that produced this entry.
 */
import { Link } from 'react-router-dom';
import type { EntryDetail, LedgerSource } from '../../api';

interface Props {
  detail: EntryDetail;
  source: Extract<LedgerSource, { kind: 'batch' }>;
}

export function BatchPanel({ detail, source }: Props) {
  const { batch, account, rawRow } = source;
  const upline = detail.parties[String(batch.uplinePartyId)];

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-small">
        <dt className="text-ink-muted">Week (Monday)</dt>
        <dd className="text-right text-ink">{batch.weekMonday}</dd>
        <dt className="text-ink-muted">Upline</dt>
        <dd className="text-right text-ink">{upline?.displayName ?? '—'}</dd>
        {account && (
          <>
            <dt className="text-ink-muted">Account</dt>
            <dd className="text-right font-mono text-ink">{account.canonicalName}</dd>
          </>
        )}
        <dt className="text-ink-muted">Batch state</dt>
        <dd className="text-right text-ink">{batch.state}</dd>
        <dt className="text-ink-muted">Description</dt>
        <dd className="text-right text-caption text-ink-muted">{detail.entry.description}</dd>
      </dl>

      {rawRow && (
        <div className="rounded-md border border-border bg-surface-raised p-3">
          <h3 className="mb-2 text-small font-semibold text-ink">Raw upline row</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-caption">
            {rawRow.panel && (
              <>
                <dt className="text-ink-muted">Panel</dt>
                <dd className="text-right text-ink">{rawRow.panel}</dd>
              </>
            )}
            <dt className="text-ink-muted">Raw name</dt>
            <dd className="text-right font-mono text-ink">{rawRow.rawAccountName}</dd>
            <dt className="text-ink-muted">Points (stated)</dt>
            <dd className="text-right font-mono text-ink">{rawRow.pointsStated}</dd>
            <dt className="text-ink-muted">Rate (stated)</dt>
            <dd className="text-right font-mono text-ink">{rawRow.rateStated}</dd>
            <dt className="text-ink-muted">Amount (stated)</dt>
            <dd className="text-right font-mono text-ink">{rawRow.amountStated}</dd>
          </dl>
        </div>
      )}

      <div>
        <Link
          to={`/batches/${batch.id}`}
          className="text-small text-brand hover:underline"
        >
          Open batch →
        </Link>
      </div>
    </div>
  );
}
